import React, { useRef, useEffect } from 'react';
import { Box, Paper, Typography, IconButton, CircularProgress } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MessageInput from './MessageInput';

const ChatMessage = ({ message, isUser, onEdit, onDelete }) => (
  <Box className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
    <Box className="flex flex-col">
      {isUser && (
        <Box className="flex justify-end mb-1">
          <IconButton size="small" onClick={onEdit}>
            <EditOutlinedIcon fontSize="small" className="text-gray-600" />
          </IconButton>
          <IconButton size="small" onClick={onDelete}>
            <DeleteOutlineOutlinedIcon fontSize="small" className="text-gray-600" />
          </IconButton>
        </Box>
      )}
      <Paper
        elevation={0}
        className={`p-4 max-w-[85%] ${
          isUser
            ? "bg-gray-100 rounded-l-xl rounded-tr-xl ml-auto"
            : "bg-gray-50 rounded-r-xl rounded-tl-xl mr-auto"
        }`}
      >
        <Typography
          variant="body1"
          className={`${
            isUser ? "text-gray-900" : "text-gray-800"
          } whitespace-pre-line`}
        >
          {message.text}
        </Typography>
      </Paper>
    </Box>
  </Box>
);

const ChatWindow = ({
  messages,
  onSendMessage,
  placeholder,
  title,
  onEditMessage,
  onDeleteMessage,
  isLoading,
  summaries = [],
  type,
  selectedProject,
}) => {
  const [input, setInput] = React.useState("");
  const messagesEndRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      onSendMessage({ text: input });
      setInput("");
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  return (
    <Box className="h-full flex flex-col bg-white rounded-lg shadow-sm">
      <Typography variant="h6" className="p-4 border-b font-semibold">
        {title}
      </Typography>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages.map((msg, index) => (
          <Box key={index}>
            {!(index === 0 && type !== "project") && (
              <ChatMessage
                message={msg}
                isUser={msg.isUser}
                onEdit={() => msg.isUser && onEditMessage(index)}
                onDelete={() => msg.isUser && onDeleteMessage(index)}
              />
            )}
            {type !== "project" && summaries[index] && (
              <Paper
                elevation={0}
                className="ml-2 mb-4 p-3 bg-yellow-50 rounded-lg max-w-[85%]"
              >
                <Typography variant="body2" className="text-gray-700">
                  <span className="font-semibold">Response Summary: </span>
                  {summaries[index]}
                </Typography>
              </Paper>
            )}
          </Box>
        ))}
        {isLoading && (
          <Box className="flex justify-center p-4">
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          borderTop: "1px solid #ccc",
          p: 2,
          display: "flex",
          gap: 1,
          position: "sticky",
          bottom: 0,
          backgroundColor: "white",
          zIndex: 10,
        }}
      >
        <MessageInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          disabled={isLoading}
          rows={5}
        />
        <IconButton
          type="submit"
          disabled={isLoading || selectedProject == null}
          sx={{
            backgroundColor: "#1a1a1a",
            color: "white",
            borderRadius: "50%",
            width: 48,
            height: 48,
            "&:hover": {
              backgroundColor: "#333333",
            },
            "&:disabled": {
              backgroundColor: "#666666",
            },
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatWindow; 